<template>
  <div class="input-wrapper">
    <label
      v-if="label"
      :for="id"
      class="input-label"
      :class="labelColor === 'black' ? 'text-gray-900' : 'text-gray-600'"
      v-text="label"
    />
    
    <!-- Input Field -->
    <div class="input-field-container" :class="borderColorClass">
      <input
        :id="id"
        :type="type"
        class="input-field"
        :placeholder="placeholder"
        v-model="internalValue"
        @input="onInputChange"
        @keydown.enter.prevent="submitImmediately"
        v-bind="$attrs"
      />
    </div>
    
    <!-- Submit Button (below input) -->
    <button
      v-if="haveButton"
      type="button"
      class="submit-button"
      :class="[buttonColorClass, { 'submit-button--disabled': !hasChanges }]"
      :disabled="!hasChanges"
      @click="handleSubmit"
    >
      {{ t('navigation.select') }}
    </button>
    
    <p v-if="haveButton" class="input-hint">
      {{ t('lifestyle.fillValueInstruction') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, inject, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  id: String,
  type: {
    type: String,
    default: 'text',
  },
  placeholder: String,
  label: String,
  labelColor: String,
  modelValue: [String, Number],
  haveButton: {
    type: Boolean,
    default: true,
  },
  autoSubmit: {
    type: Boolean,
    default: false,
  },
  autoSubmitDelay: {
    type: Number,
    default: 1000,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

// Local state reflects prop until user confirms
const internalValue = ref<string>(String(props.modelValue ?? ''));

// Track the last submitted value to know if there are changes
const lastSubmittedValue = ref<string>(String(props.modelValue ?? ''));

// Flag to prevent watch from overwriting during submission
const isSubmitting = ref(false);
let autoSubmitTimer: ReturnType<typeof setTimeout> | null = null;

// Check if the current value is different from last submitted
const hasChanges = computed(() => {
  return internalValue.value !== lastSubmittedValue.value;
});

// When user types, this enables the button
function onInputChange() {
  if (!props.autoSubmit) return;

  clearAutoSubmitTimer();
  autoSubmitTimer = setTimeout(() => {
    autoSubmitTimer = null;
    const value = internalValue.value.trim();
    const numericValue = Number(value);

    if (value !== '' && Number.isFinite(numericValue)) {
      handleSubmit();
    }
  }, Math.max(250, props.autoSubmitDelay));
}

function clearAutoSubmitTimer() {
  if (autoSubmitTimer !== null) {
    clearTimeout(autoSubmitTimer);
    autoSubmitTimer = null;
  }
}

function submitImmediately() {
  clearAutoSubmitTimer();
  handleSubmit();
}

onBeforeUnmount(clearAutoSubmitTimer);

// Sync when parent value changes externally
watch(
  () => props.modelValue,
  (val, oldVal) => {
    // Don't update if we're in the middle of a submission
    if (isSubmitting.value) {
      return;
    }
    
    const newVal = String(val ?? '');
    const currentInternal = internalValue.value;
    
    // Only update if the new value is meaningful and different
    // Don't overwrite user input with empty/null values from API race conditions
    if (newVal === '' && currentInternal !== '') {
      // Ignore empty values that would clear user input
      return;
    }
    
    // Only sync if the value actually changed from what we have
    if (newVal !== currentInternal) {
      internalValue.value = newVal;
      lastSubmittedValue.value = newVal;
    }
  }
);

// Theme color for styling
const colorScheme = inject<'purple' | 'yellow' | 'green' | 'blue'>('colorScheme', 'purple');

const borderColorClass = computed(() => ({
  'border-purple': colorScheme === 'purple',
  'border-yellow': colorScheme === 'yellow',
  'border-green': colorScheme === 'green',
  'border-blue': colorScheme === 'blue',
}));

const buttonColorClass = computed(() => ({
  'button-purple': colorScheme === 'purple',
  'button-yellow': colorScheme === 'yellow',
  'button-green': colorScheme === 'green',
  'button-blue': colorScheme === 'blue',
}));

// Submit handler - disables button after click
function handleSubmit() {
  if (!hasChanges.value) return;
  
  // Set submitting flag to prevent watch from interfering
  isSubmitting.value = true;
  
  // Update the last submitted value to disable button
  lastSubmittedValue.value = internalValue.value;
  
  emit('update:modelValue', internalValue.value);
  emit('change', internalValue.value);
  
  // Reset submitting flag after a short delay to allow the value to propagate
  setTimeout(() => {
    isSubmitting.value = false;
  }, 500);
}
</script>

<style scoped>
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

/* Input field container - WIDE */
.input-field-container {
  width: 100%;
  position: relative;
  border-radius: 12px;
  background: white;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;
  overflow: hidden;
}

.input-field-container:focus-within {
  border-color: currentColor;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
}

/* Color variants for border */
.input-field-container.border-purple:focus-within {
  border-color: #8d3e79;
  box-shadow: 0 0 0 3px rgba(141, 62, 121, 0.1);
}

.input-field-container.border-yellow:focus-within {
  border-color: #ca8a04;
  box-shadow: 0 0 0 3px rgba(202, 138, 4, 0.1);
}

.input-field-container.border-green:focus-within {
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
}

.input-field-container.border-blue:focus-within {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
}

/* Input field itself */
.input-field {
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  background: transparent;
  outline: none;
  color: #1f2937;
  text-align: left;
}

.input-field::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

/* Submit button - BELOW input, full width */
.submit-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.submit-button:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.95);
}

/* Disabled state */
.submit-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  filter: grayscale(0.3);
}

/* Button color variants */
.submit-button.button-purple {
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  box-shadow: 0 4px 14px rgba(147, 51, 234, 0.35);
}

.submit-button.button-purple:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(147, 51, 234, 0.45);
}

.submit-button.button-yellow {
  background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%);
  box-shadow: 0 4px 14px rgba(234, 179, 8, 0.35);
}

.submit-button.button-yellow:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(234, 179, 8, 0.45);
}

.submit-button.button-green {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35);
}

.submit-button.button-green:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.45);
}

.submit-button.button-blue {
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  box-shadow: 0 4px 14px rgba(20, 184, 166, 0.35);
}

.submit-button.button-blue:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(20, 184, 166, 0.45);
}

/* Hint text */
.input-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
  font-style: italic;
  text-align: left;
}
</style>
